import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Loader2, PawPrint } from "lucide-react";
import api from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { useSiteContent } from "../hooks/useSiteContent";

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function Blog() {
  const content = useSiteContent();
  const pg = content?.pages?.blog || {};
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "Blog | Quest K9's Detection Dogs";
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <main className="bg-brand-obsidian min-h-screen pt-28 sm:pt-32" data-testid="blog-page">
      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3">
              <span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "The Journal"}
            </Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {pg.title || "News from the"} <span className="text-brand-crimson">{pg.title_accent || "kennels"}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-slate-400 leading-relaxed">
              {pg.intro || "Updates, deployment notes and behind-the-scenes stories from Quest K9's Detection Dogs."}
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts === null && (
              <div className="col-span-full flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="blog-loading-spinner" />
              </div>
            )}
            {posts !== null && posts.length === 0 && (
              <div className="col-span-full rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="blog-empty">
                {pg.empty || "No posts yet - check back soon."}
              </div>
            )}
            {posts?.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.07}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-card-${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-lg border border-brand-line bg-brand-surface transition-all duration-300 hover:border-brand-crimson/70 hover:shadow-[0_0_40px_rgba(200,16,46,0.15)]"
                >
                  <div className="relative h-52 overflow-hidden bg-brand-obsidian">
                    {post.cover_image ? (
                      <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="h-full flex items-center justify-center text-brand-crimson/40">
                        <PawPrint className="h-10 w-10" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian via-transparent to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="flex items-center gap-2 text-[11px] text-brand-crimson font-eyebrow uppercase tracking-[0.2em] font-bold">
                      <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(post.published_at || post.created_at)}
                    </p>
                    <h2 className="mt-3 font-display text-xl font-bold text-white leading-snug">{post.title}</h2>
                    {post.excerpt && <p className="mt-2 text-sm text-slate-400 leading-relaxed">{post.excerpt}</p>}
                    <span className="mt-auto pt-4 inline-flex items-center gap-2 text-[11px] font-eyebrow uppercase tracking-[0.2em] font-bold text-white/70 group-hover:text-brand-crimson transition-colors">
                      Read Post <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
